import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { comPrefixo } from 'volto-ministerio-publico-federal/helpers/Url/subpath';
import React from 'react';
import useFormattedDate from 'volto-ministerio-publico-federal/hooks/useFormattedDate';
import { useTemaHref } from 'volto-ministerio-publico-federal/hooks/useNewsFilters';
import { isValidDate } from 'volto-ministerio-publico-federal/utils/isValidDate';

type ImageScale = {
  download?: string;
  width?: number;
  height?: number;
};

export type NewsCardImage = {
  'content-type'?: string;
  download?: string;
  filename?: string;
  width?: number;
  height?: number;
  /**
   * Presente quando a imagem mora num objeto `Image` filho (notícia que usa
   * `preview_image_link`): os `download` das escalas são relativos a ELE, e
   * não à notícia.
   */
  base_path?: string;
  scales?: {
    icon?: ImageScale;
    mini?: ImageScale;
    preview?: ImageScale;
    thumb?: ImageScale;
    tile?: ImageScale;
    teaser?: ImageScale;
    large?: ImageScale;
    larger?: ImageScale;
    great?: ImageScale;
    huge?: ImageScale;
  };
};

/**
 * Um mesmo brain de notícia chega em dois formatos: pelo `relatedItems` do
 * conteúdo (onde `tema`/`unidadeOrigem` já vêm resolvidos como objeto pelo
 * NoticiaJSONSummarySerializer) e pelo `@search` (onde o mesmo campo pode
 * chegar como string simples e a unidade como o metadata `unit_title`).
 * Os tipos abaixo aceitam as duas formas e os getters normalizam.
 */
type VocabularyValue = string | { title?: string; token?: string };

export interface NewsCardItem {
  '@id': string;
  '@type'?: string;
  UID?: string;
  title: string;
  description?: string;
  effective?: string;
  tema?: VocabularyValue;
  unidadeOrigem?: VocabularyValue;
  unit_title?: string;
  tituloAlternativo?: string;
  /** Chave de `image_scales` onde está a imagem deste item. */
  image_field?: string;
  image_scales?: Record<string, NewsCardImage[] | undefined>;
  /** Texto alternativo cadastrado para a imagem de destaque (`image`). */
  descricaoImagem?: string | null;
  /** Texto alternativo cadastrado para a imagem de pré-visualização. */
  preview_alt_link?: string | null;
}

/** Quantidade máxima de linhas de descrição exibidas no card. */
const MAX_DESCRIPTIONS = 2;

export const getVocabularyTitle = (value?: VocabularyValue): string | null => {
  if (!value) {
    return null;
  }

  return typeof value === 'string' ? value : value.title || null;
};

/**
 * O valor que a busca e a querystring da listagem entendem. Para `tema` o token
 * coincide com o rótulo, mas para `unidadeOrigem` é a sigla (`prrj`) — por isso
 * quem monta uma consulta precisa disto, e não de `getVocabularyTitle`.
 */
export const getVocabularyToken = (
  value?: VocabularyValue,
): string | undefined => {
  if (!value) {
    return undefined;
  }

  return (typeof value === 'string' ? value : value.token) || undefined;
};

export const getImageUrl = (item: NewsCardItem): string | null => {
  // `image` é o leadimage da notícia; quando a imagem vem de um `Image` filho
  // (`preview_image_link`, comum no acervo migrado), a chave é outra e quem
  // diz qual é o metadado `image_field` da busca.
  const image = item.image_scales?.[item.image_field || 'image']?.[0];

  if (!image) {
    return null;
  }

  const imageUrl =
    image.scales?.preview?.download ||
    image.scales?.teaser?.download ||
    image.scales?.large?.download ||
    image.scales?.thumb?.download ||
    image.download;

  if (!imageUrl) {
    return null;
  }

  // Toda URL que vem do backend passa pelo flatten: no servidor o Volto fala
  // com o backend pelo nome interno (`plone-backend:8080`) e o `@id` dos
  // relacionados vem com esse host. Sem o flatten, a imagem da notícia
  // relacionada saía no HTML público com o host interno, que o navegador
  // não resolve (AB#11097; a imagem de destaque já fazia isso).
  if (/^https?:\/\//i.test(imageUrl)) {
    return flattenToAppURL(imageUrl);
  }

  // Com `base_path` o download é relativo ao Image filho; sem ele, à notícia.
  const base = flattenToAppURL(image.base_path || item['@id']);

  return `${base}/${imageUrl.replace(/^\/+/, '')}`;
};

/**
 * Texto alternativo da imagem do card: o que a redação cadastrou para a imagem
 * que o card mostra (AB#11375, decisão da SECOM em 21/10). Quando a imagem
 * vem da pré-visualização (`image_field` diferente de `image`) vale o
 * `preview_alt_link`; quando é a imagem de destaque, o `descricaoImagem`. Sem
 * cadastro, o `alt` fica vazio e o leitor de tela segue para o título do card,
 * que está logo ao lado.
 */
export const getImageAlt = (item: NewsCardItem): string => {
  const field = item.image_field || 'image';
  const alt =
    field === 'image'
      ? item.descricaoImagem
      : item.preview_alt_link || item.descricaoImagem;

  return (alt || '').trim();
};

/**
 * No vocabulário de temas o token É o título ("Meio Ambiente"), então a forma
 * string já serve para exibir.
 */
export const getCategory = (item: NewsCardItem): string | null =>
  getVocabularyTitle(item.tema);

/**
 * Já em `unidadeOrigem` o token é a sigla ("prrj"), e o nome por extenso vem
 * no metadata `unit_title` ("Procuradoria da República no Rio de Janeiro").
 *
 * Por isso a ordem aqui NÃO é a mesma de `getCategory`: só o formato objeto
 * (como chega em `relatedItems`, resolvido pelo serializer) traz o título;
 * quando vem string, é sigla, e quem tem o nome legível é o `unit_title`.
 * Inverter isso faz o card mostrar "prrj" para o leitor.
 */
export const getLocation = (item: NewsCardItem): string | null => {
  if (item.unidadeOrigem && typeof item.unidadeOrigem !== 'string') {
    return item.unidadeOrigem.title || item.unit_title || null;
  }

  // Sigla crua só como último recurso — melhor que não mostrar nada.
  return item.unit_title || item.unidadeOrigem || null;
};

export const getDescriptions = (item: NewsCardItem): string[] => {
  const descriptions: string[] = [];

  if (item.description) {
    descriptions.push(item.description);
  }

  if (item.tituloAlternativo) {
    descriptions.push(item.tituloAlternativo);
  }

  return descriptions.slice(0, MAX_DESCRIPTIONS);
};

export interface NewsCardRowProps {
  item: NewsCardItem;
  /**
   * Prefixo BEM das classes do card. O default tem estilo próprio em
   * `components/_news-card-row.scss`; a seção "Veja também" da notícia passa
   * `mpf-news__related` para continuar usando as regras de `_news-view.scss`.
   */
  baseClass?: string;
  /**
   * "há X" depende de `Date.now()`, que difere entre o render do servidor e a
   * hidratação — quem monta a lista controla quando a data relativa aparece.
   */
  showRelativeDate?: boolean;
  /**
   * Unidade a preservar no link do tema. O bloco "Notícias por tema" passa a
   * que o editor configurou; sem isso, vale a unidade que estiver na URL.
   */
  unitContext?: string;
  /**
   * Exibe a unidade responsável na linha de metadados. `false` na página de
   * notícia (relacionadas/veja também), onde a lista já é do mesmo contexto e
   * repetir a unidade em cada card é ruído.
   */
  showUnit?: boolean;
}

export const NewsCardRow: React.FC<NewsCardRowProps> = ({
  item,
  baseClass = 'mpf-news-card-row',
  showRelativeDate = true,
  unitContext,
  showUnit = true,
}) => {
  const { formatRelative } = useFormattedDate();
  const temaHref = useTemaHref(unitContext);

  const image = getImageUrl(item);
  const imageAlt = getImageAlt(item);
  const category = getCategory(item);
  const location = showUnit ? getLocation(item) : null;
  const descriptions = getDescriptions(item);

  const date =
    showRelativeDate && isValidDate(item.effective)
      ? formatRelative(item.effective as string, { capitalize: true })
      : null;

  return (
    <div className={`${baseClass}-link`}>
      {image && (
        <img
          src={comPrefixo(image)}
          alt={imageAlt}
          className={`${baseClass}-image`}
        />
      )}

      <div className={`${baseClass}-content`}>
        {category && (
          <span className={`${baseClass}-category`}>
            <UniversalLink href={temaHref(category)}>{category}</UniversalLink>
          </span>
        )}

        <h3 className={`${baseClass}-title`}>
          <UniversalLink href={flattenToAppURL(item['@id'])}>
            {item.title}
          </UniversalLink>
        </h3>

        <div className={`${baseClass}-description`}>
          {descriptions.map((desc, index) => (
            <p key={index}>{desc}</p>
          ))}
        </div>

        <div className={`${baseClass}-meta`}>
          {date && <span>{date}</span>}

          {date && location && (
            <span aria-hidden="true" className={`${baseClass}-meta-separator`}>
              •
            </span>
          )}

          {location && <span>{location}</span>}
        </div>
      </div>
    </div>
  );
};

export default NewsCardRow;
